import React from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import './Badge.css';

/**
 * Badge Component
 * Status indicators with various variants
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Badge content
 * @param {string} props.variant - Visual variant: 'success', 'error', 'warning', 'info', 'neutral'
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg'
 * @param {boolean} props.dot - Show dot indicator
 * @param {string} props.className - Additional CSS classes
 */
const Badge = ({
  children,
  variant = 'neutral',
  size = 'md',
  dot = false,
  className = '',
  ...props
}) => {
  const badgeClasses = `
    badge
    badge--${variant}
    badge--${size}
    ${dot ? 'badge--with-dot' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <motion.span
      className={badgeClasses}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      {...props}
    >
      {dot && <span className={`badge-dot badge-dot--${variant}`} />}
      {children}
    </motion.span>
  );
};

Badge.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['success', 'error', 'warning', 'info', 'neutral']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  dot: PropTypes.bool,
  className: PropTypes.string,
};

export default Badge;
